import React, { useEffect, useState, useRef, useCallback } from 'react';
import styles from './ModalInputField.module.css';
import useTheme from 'config/useTheme';

const ModalInputField = ({
    autoFocus = false,
    type = 'text',
    placeholder,
    value,
    setValue,
    validator,
}) => {
    const { dark } = useTheme();

    const [inputValue, setInputValue] = useState(value ?? '');
    const [isValid, setIsValid] = useState(true);
    const [wasTouched, setWasTouched] = useState(false);
    const [showValidation, setShowValidation] = useState(false);

    const debounceRef = useRef();

    // ✅ Sync local input if external value changes (but only if different)
    useEffect(() => {
        if (value !== inputValue) {
            setInputValue(value ?? '');
        }
    }, [value]);

    // ✅ Debounced validator and updater — only run if value changed
    useEffect(() => {
        clearTimeout(debounceRef.current);

        debounceRef.current = setTimeout(() => {
            // ⚠️ Prevent unnecessary setValue loop
            if (setValue && inputValue !== value) {
                setValue(inputValue);
            }

            if (validator) {
                const valid = validator(inputValue);
                setIsValid(valid);
            }
        }, 400);

        return () => clearTimeout(debounceRef.current);
    }, [inputValue, value, setValue, validator]);

    const handleChange = useCallback((e) => {
        const newVal = e.target.value;
        setInputValue(newVal);

        if (!wasTouched) {
            setWasTouched(true);
        }
    }, [wasTouched]);

    const handleBlur = useCallback(() => {
        setShowValidation(true);
    }, []);

    return (
        <div
            className={`${styles.input_box} ${showValidation && !isValid ? styles.invalid : ''
                } ${dark ? styles.dark : ''}`}
        >
            <input
                autoFocus={autoFocus}
                type={type}
                placeholder=""
                value={inputValue}
                onChange={handleChange}
                onBlur={handleBlur}
                required
            />
            <span>{placeholder}</span>
        </div>
    );
};

export default React.memo(ModalInputField);
